import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { translate } from 'react-i18next'
import {
  DisplayText,
  ResourceList,
  Icon,
  Caption,
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  ButtonGroup,
} from './../../components'
import ResidentsScene from './../scenes/ResidentsScene'
import ContractScene from './../scenes/ContractScene'
import RentIntentionScene from './../scenes/RentIntentionScene'
import FinancialScene from './../scenes/FinancialScene'
import DocumentsScene from './../scenes/DocumentsScene'
import flowSelectors from './../../selectors/flowSelector'
import { validateRentIntention, validateContract, validateIncome, validateDocuments } from './../../utils/validation'
import * as translationService from './../../services/translationService'
import { getApplicationProfile } from '../../actions/FlowAction'
import { isBrusselsZipCode } from '../../services/routeService'

const validators = {
  residents: () => true,
  contract: validateContract,
  rentIntention: validateRentIntention,
  income: validateIncome,
  documents: validateDocuments,
}

const getIsRequired = application => ({
  residents: true,
  contract: true,
  rentIntention: true,
  income: application.isIncomeRequired,
  documents: true,
})

const scenes = {
  residents: <ResidentsScene />,
  contract: <ContractScene />,
  rentIntention: <RentIntentionScene />,
  income: <FinancialScene />,
  documents: <DocumentsScene />,
}

const getSceneByPrefix = (prefix, summary, application, isBrusselsZip) => {
  if (!prefix) {
    return null
  }

  const casedPrefix = prefix[0].toUpperCase() + prefix.substring(1)

  return {
    title: translationService[`step${casedPrefix}`](),
    summary,
    scene: scenes[prefix],
    isValid: validators[prefix](application, true, isBrusselsZip),
    isRequired: getIsRequired(application)[prefix],
  }
}

class ProfileView extends Component {
  static propTypes = {
    application: PropTypes.any,
    property: PropTypes.any,
    user: PropTypes.any,
    items: PropTypes.array,
    checkValidity: PropTypes.bool,
    isUploading: PropTypes.bool,
    getApplicationProfile: PropTypes.func,
    step: PropTypes.number,
    isFirstPhase: PropTypes.bool,
    isIncomeRequired: PropTypes.bool,
    shouldIncludeIncomeStatement: PropTypes.bool,
  }
  static defaultProps = {
    items: ['residents', 'contract', 'rentIntention', 'income', 'documents'],
  }

  state = {
    isSceneModalOpen: false,
    mainError: null,
    currentItem: null,
    isBrusselsZip: false,
  }

  modalRef = {}

  componentDidMount() {
    const { application, property } = this.props
    this.setState({
      originalApplication: application,
      isBrusselsZip: isBrusselsZipCode(property.zip),
    })
  }

  saveApplication = () => {
    this.modalRef
      .onSubmit()
      .then(() => {
        this.toggleModal({}, false)
      })
      .catch(() => {
        // Do nothing, error is handled inside the modal.
      })
  }

  openScene = currentItem => {
    this.setState(
      {
        currentItem,
      },
      this.toggleModal,
    )
  }

  toggleModal = () => {
    this.setState(
      prevState => {
        return {
          isSceneModalOpen: !prevState.isSceneModalOpen,
        }
      },
      () => this.props.getApplicationProfile(this.props.user),
    )
  }

  renderModal = item => {
    if (!item) return null

    return (
      <Modal
        isOpen={this.state.isSceneModalOpen}
        onRequestClose={this.toggleModal}
        title={item.title}
        isRequired={item.isRequired}>
        <ModalBody id="sceneModal">
          {React.cloneElement(item.scene, {
            inModal: true,
            checkForError: true,
            onRef: ref => (this.modalRef = ref),
          })}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button onClick={this.toggleModal}>{translationService.buttonCancel()}</Button>
            <Button onClick={() => this.saveApplication()} primary loading={this.props.isUploading}>
              {translationService.buttonSave()}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>
    )
  }

  renderItem = (item, checkValidity) => {
    const { isFirstPhase, isIncomeRequired, shouldIncludeIncomeStatement } = this.props
    const scene = getSceneByPrefix(
      item,
      this.props[`${item}Summary`],
      {
        ...this.props.application,
        isFirstPhase,
        isIncomeRequired,
        shouldIncludeIncomeStatement,
      },
      this.state.isBrusselsZip,
    )

    return (
      <ResourceList.Item
        media={
          checkValidity ? (
            <Icon source={scene.isValid ? 'shieldCheck' : 'shieldWarning'} color={scene.isValid ? 'green' : 'red'} />
          ) : (
            <Icon source="shieldCheck" />
          )
        }
        onClick={() => this.openScene(scene)}
        actions={[
          {
            content: translationService.buttonEdit(),
            onClick: () => this.openScene(scene),
            primary: false,
            plain: true,
          },
        ]}>
        <div className="d-md-flex align-items-center">
          <DisplayText size="extraSmall" color={checkValidity ? (scene.isValid ? 'green' : 'red') : 'black'}>
            {scene.title}
          </DisplayText>
          <Caption>
            <span dangerouslySetInnerHTML={{ __html: scene.summary }} />
          </Caption>
        </div>
      </ResourceList.Item>
    )
  }
  render() {
    const { checkValidity = true } = this.props
    return (
      <React.Fragment>
        <ResourceList items={this.props.items} renderItem={item => this.renderItem(item, checkValidity)} />

        {this.renderModal(this.state.currentItem)}
      </React.Fragment>
    )
  }
}

const mapStateToProps = ({ FlowReducer, AuthReducer, AppReducer }) => {
  const { user } = AuthReducer
  const { isUploading } = AppReducer
  const { step, property } = FlowReducer
  return {
    isUploading,
    application: flowSelectors.application(FlowReducer),
    residentsSummary: flowSelectors.residentsSummary(FlowReducer),
    contractSummary: flowSelectors.contractSummary(FlowReducer),
    rentIntentionSummary: flowSelectors.rentIntentionSummary(FlowReducer),
    incomeSummary: flowSelectors.incomeSummary(FlowReducer),
    documentsSummary: flowSelectors.documentsSummary(FlowReducer),
    isFirstPhase: flowSelectors.getIsFirstPhase(FlowReducer),
    isIncomeRequired: flowSelectors.getIsIncomeRequired(FlowReducer),
    shouldIncludeIncomeStatement: flowSelectors.getShouldIncludeIncomeStatement(FlowReducer),
    step,
    property,
    user,
  }
}

const mapDispatchToProps = dispatch => ({
  getApplicationProfile: user => dispatch(getApplicationProfile(user)),
})

export default compose(
  translate('translations'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(ProfileView)



// WEBPACK FOOTER //
// ./src/views/Profile/Profile.js